///
/// EarthBrowser
///
/// Created by Matt Giger on 9/11/2026
/// Copyright (c) 2026 Geodata Labs
///

import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { cube } from "@stovokor/math/cube"
import { type sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

/** A point on a line in geographic coordinates (radians) with optional elevation (km) */
export interface GeoLinePoint {
	lon: number
	lat: number
	elev?: number
}

export interface GeoPolylineOptions {
	/** Many independent lines batched into a single draw call */
	lines: GeoLinePoint[][]

	/**
	 * Segments longer than this (radians) are subdivided so lines follow the
	 * curvature of the globe and pick up per-vertex terrain elevation
	 */
	maxSegmentRadians?: number

	/**
	 * Terrain height (km) at a geographic position (radians). Sampled per
	 * vertex after subdivision so lines drape over the displaced terrain.
	 * The vertex shader applies the same elevation multiplier as the tiles.
	 */
	elevationSampler?: (lon: number, lat: number) => number

	/**
	 * Local line density (km of line per km²) at a geographic position
	 * (radians), sampled per vertex after subdivision. The shader turns it
	 * into on-screen line spacing to fade out areas too dense to read
	 */
	densitySampler?: (lon: number, lat: number) => number
}

// Padding added to the bounding box for the elevation lift applied in the shader (km)
const cBoundsPadding = 24

/**
 * Batched polyline geometry in geographic coordinates for vector tile rendering.
 *
 * Unlike PolylineGeometry (one line, world-space positions), this batches many
 * line features into one buffer and stores (lon, lat, elev) so the vertex
 * shader performs the same geo-to-cartesian transform as the terrain tiles.
 * Line width is a per-bucket uniform rather than a per-vertex attribute.
 *
 * Screen-space extrusion technique from:
 * https://cesium.com/blog/2013/04/22/robust-polyline-rendering-with-webgl/
 */
export class GeoPolylineGeometry implements Geometry {
	// Current vertex geographic position (lon, lat, elev)
	public currentGeo: VertexAttribute

	// Previous vertex position (for computing miter joins)
	public previousGeo: VertexAttribute

	// Next vertex position (for computing miter joins)
	public nextGeo: VertexAttribute

	// Extrusion side and quad end
	// Encoded as: sign is direction (-1 or 1), magnitude is which end of its
	// quad the vertex is (1 = leading, 2 = trailing)
	public direction: VertexAttribute

	// Great-circle distance (km) from the start of the vertex's line, for
	// dash patterns and flow animation in the shader
	public distance: VertexAttribute

	// Local line density (km/km²) around the vertex, for density fading
	public density: VertexAttribute

	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer

	public mode: DrawMode = DrawMode.Triangles
	public available: boolean = true
	public bounds = new cube()
	public boundingSphere: sphere

	/** Total number of vertices in the batch */
	public vertexCount: number = 0

	constructor(context: StovokorContext, options: GeoPolylineOptions) {
		this.currentGeo = new VertexAttribute(context, 3)
		this.previousGeo = new VertexAttribute(context, 3)
		this.nextGeo = new VertexAttribute(context, 3)
		this.direction = new VertexAttribute(context, 1)
		this.distance = new VertexAttribute(context, 1)
		this.density = new VertexAttribute(context, 1)
		this.indexBuffer = new IndexBuffer(context)

		const maxSegment = options.maxSegmentRadians ?? 0
		const sampler = options.elevationSampler
		const densitySampler = options.densitySampler

		for (const line of options.lines) {
			let points = maxSegment > 0 ? GeoPolylineGeometry.subdivide(line, maxSegment) : line
			if (sampler) {
				points = points.map(pt => ({ lon: pt.lon, lat: pt.lat, elev: sampler(pt.lon, pt.lat) }))
			}
			if (points.length >= 2) {
				this.buildLine(points, densitySampler)
			}
		}

		this.vertexBuffer = new VertexBuffer(context, [
			this.currentGeo,
			this.previousGeo,
			this.nextGeo,
			this.direction,
			this.distance,
			this.density
		])

		// Pad bounds for the shader-side elevation lift
		if (this.bounds.finite) {
			this.bounds.update(this.bounds.minx - cBoundsPadding, this.bounds.miny - cBoundsPadding, this.bounds.minz - cBoundsPadding)
			this.bounds.update(this.bounds.maxx + cBoundsPadding, this.bounds.maxy + cBoundsPadding, this.bounds.maxz + cBoundsPadding)
		}
		this.boundingSphere = this.bounds.sphere
	}

	/** Insert interpolated points so no segment exceeds maxSegment radians */
	private static subdivide(points: GeoLinePoint[], maxSegment: number): GeoLinePoint[] {
		const result: GeoLinePoint[] = []
		for (let i = 0; i < points.length - 1; i++) {
			const a = points[i]
			const b = points[i + 1]
			result.push(a)

			const dLon = b.lon - a.lon
			const dLat = b.lat - a.lat
			const dist = Math.sqrt(dLon * dLon + dLat * dLat)
			const steps = Math.floor(dist / maxSegment)
			for (let s = 1; s <= steps; s++) {
				const t = s / (steps + 1)
				result.push({
					lon: a.lon + dLon * t,
					lat: a.lat + dLat * t,
					elev: (a.elev ?? 0) + ((b.elev ?? 0) - (a.elev ?? 0)) * t
				})
			}
		}
		result.push(points[points.length - 1])
		return result
	}

	/** Great-circle distance between two geographic points (km) */
	public static distanceKm(a: GeoLinePoint, b: GeoLinePoint): number {
		const sinLat = Math.sin((b.lat - a.lat) / 2)
		const sinLon = Math.sin((b.lon - a.lon) / 2)
		const h = sinLat * sinLat + Math.cos(a.lat) * Math.cos(b.lat) * sinLon * sinLon
		return 2 * wgs.a * Math.asin(Math.min(1, Math.sqrt(h)))
	}

	private buildLine(points: GeoLinePoint[], densitySampler?: (lon: number, lat: number) => number): void {
		const segmentCount = points.length - 1
		let distance = 0
		let density = densitySampler ? densitySampler(points[0].lon, points[0].lat) : 0

		for (let i = 0; i < segmentCount; i++) {
			const curr = points[i]
			const next = points[i + 1]
			const nextDistance = distance + GeoPolylineGeometry.distanceKm(curr, next)
			const nextDensity = densitySampler ? densitySampler(next.lon, next.lat) : 0

			// Get previous and next positions for join computation. At line
			// endpoints the missing neighbor duplicates the vertex itself,
			// which the shader detects and extrudes perpendicular to the one
			// real segment instead of mitering.
			const prev = i === 0 ? curr : points[i - 1]
			const nextNext = i === segmentCount - 1 ? next : points[i + 2]

			// Create 4 vertices for this segment (two per end, extruded to opposite sides)
			const baseIndex = this.vertexCount
			this.addVertex(curr, prev, next, -1, false, distance, density)
			this.addVertex(curr, prev, next, 1, false, distance, density)
			this.addVertex(next, curr, nextNext, -1, true, nextDistance, nextDensity)
			this.addVertex(next, curr, nextNext, 1, true, nextDistance, nextDensity)
			distance = nextDistance
			density = nextDensity

			this.indexBuffer.quad(
				baseIndex + 0,
				baseIndex + 2,
				baseIndex + 3,
				baseIndex + 1
			)
		}
	}

	private addVertex(
		pos: GeoLinePoint,
		prev: GeoLinePoint,
		next: GeoLinePoint,
		dir: number,
		trailing: boolean,
		distanceKm: number,
		density: number
	): void {
		this.currentGeo.add(pos.lon, pos.lat, pos.elev ?? 0)
		this.previousGeo.add(prev.lon, prev.lat, prev.elev ?? 0)
		this.nextGeo.add(next.lon, next.lat, next.elev ?? 0)

		// Sign is extrusion side, magnitude is which end of its quad the
		// vertex is (1 = leading, 2 = trailing), matching the shader decode
		this.direction.add(dir * (trailing ? 2.0 : 1.0))
		this.distance.add(distanceKm)
		this.density.add(density)
		this.vertexCount++

		// Bounds are in world space, so convert the geographic position
		const cartesian = new geographic(pos.lon, pos.lat, pos.elev ?? 0).cartesian
		this.bounds.update(cartesian.x, cartesian.y, cartesian.z)
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}
